import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Speaker } from '../../types/conference';
import { API_BASE_URL } from '../../config';
import { formatImageUrl } from '../../utils/imageUtils';

interface SpeakerFormProps {
  speaker: Partial<Speaker>;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  submitLabel: string;
  onFieldChange: (field: string, value: any) => void;
  onCancel?: () => void;
  onImageUpload?: (speakerId: string, file: File) => Promise<void>;
}

const SpeakerForm: React.FC<SpeakerFormProps> = React.memo(({ 
  speaker, 
  onSubmit, 
  submitLabel, 
  onFieldChange, 
  onCancel,
  onImageUpload
}) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !speaker.id || !onImageUpload) return;

    setUploading(true);
    try {
      await onImageUpload(speaker.id, file);
    } catch (error) {
      console.error('Image upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6 p-6 bg-gray-50 rounded-lg">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Name *</label>
          <input
            type="text"
            value={speaker.name || ''}
            onChange={(e) => onFieldChange('name', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Title/Position</label>
          <input
            type="text"
            value={speaker.title || ''}
            onChange={(e) => onFieldChange('title', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Company</label>
          <input
            type="text"
            value={speaker.company || ''}
            onChange={(e) => onFieldChange('company', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">LinkedIn URL</label>
          <input
            type="url"
            value={speaker.linkedin_url || ''}
            onChange={(e) => onFieldChange('linkedin_url', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Bio Fields */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Full Bio</label>
        <textarea
          value={speaker.bio || ''}
          onChange={(e) => onFieldChange('bio', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          rows={4}
          placeholder="Full speaker biography"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Short Bio</label>
        <textarea
          value={speaker.bio_short || ''}
          onChange={(e) => onFieldChange('bio_short', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          rows={2}
          placeholder="Brief bio for speaker cards (1-2 sentences)"
        />
      </div>

      {/* Talk Info */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Talk Title</label>
        <input
          type="text"
          value={speaker.talk_title || ''}
          onChange={(e) => onFieldChange('talk_title', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          placeholder="Title of their presentation"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Talk Abstract</label>
        <textarea
          value={speaker.talk_abstract || ''}
          onChange={(e) => onFieldChange('talk_abstract', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          rows={3}
          placeholder="Brief description of their talk"
        />
      </div>

      {/* Image Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Speaker Photo</label>
        <div className="mt-1 flex items-center space-x-4">
          {speaker.image && (
            <img
              src={formatImageUrl(speaker.image)}
              alt={speaker.name}
              className="h-16 w-16 rounded-full object-cover"
            />
          )}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || !speaker.id}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : 'Upload Photo'}
            </button>
            {!speaker.id && (
              <p className="text-xs text-gray-500 mt-1">Save speaker first to upload photo</p>
            )}
          </div>
        </div>
      </div>

      {/* Visibility Control */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="display_on_public"
          checked={speaker.display_on_public || false}
          onChange={(e) => onFieldChange('display_on_public', e.target.checked)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="display_on_public" className="ml-2 block text-sm text-gray-900">
          Display on public speakers page
        </label>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
});

SpeakerForm.displayName = 'SpeakerForm';

interface SpeakerManagementProps {
  speakers: Speaker[];
  onCreateSpeaker: (speaker: Omit<Speaker, 'id'>) => Promise<void>;
  onUpdateSpeaker: (speaker: Speaker) => Promise<void>;
  onDeleteSpeaker: (id: string) => Promise<void>;
  onImageUpload: (speakerId: string, file: File) => Promise<void>;
  onSpeakerUpdated?: () => void;
}

const SpeakerManagement: React.FC<SpeakerManagementProps> = ({
  speakers,
  onCreateSpeaker,
  onUpdateSpeaker,
  onDeleteSpeaker,
  onImageUpload,
  onSpeakerUpdated,
}) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingSpeaker, setEditingSpeaker] = useState<Speaker | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVisible, setFilterVisible] = useState<'all' | 'visible' | 'hidden'>('all');
  const [newSpeaker, setNewSpeaker] = useState<Omit<Speaker, 'id'>>({
    name: '',
    bio: '',
    title: '',
    company: '',
    linkedin_url: '',
    bio_short: '',
    talk_title: '',
    talk_abstract: '',
    display_on_public: false,
  });


  const handleSubmitNew = async (e: React.FormEvent) => {
    e.preventDefault();
    await onCreateSpeaker(newSpeaker);
    setIsAddingNew(false);
    resetNewSpeaker();
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingSpeaker) {
      await onUpdateSpeaker(editingSpeaker);
      setEditingSpeaker(null);
    }
  };

  const resetNewSpeaker = () => {
    setNewSpeaker({
      name: '',
      bio: '',
      title: '',
      company: '',
      linkedin_url: '',
      bio_short: '',
      talk_title: '',
      talk_abstract: '',
      display_on_public: false,
    });
  };

  const handleFieldChange = useCallback((field: string, value: any) => {
    if (editingSpeaker) {
      setEditingSpeaker(prev => ({
        ...prev!,
        [field]: value
      }));
    } else {
      setNewSpeaker(prev => ({
        ...prev,
        [field]: value
      }));
    }
  }, [editingSpeaker]);

  const handleCancel = useCallback(() => {
    setIsAddingNew(false);
    setEditingSpeaker(null);
    resetNewSpeaker();
  }, []);

  // Filter speakers based on search and visibility
  const filteredSpeakers = speakers.filter(speaker => {
    const matchesSearch = speaker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         speaker.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         speaker.title?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterVisible === 'all' || 
                         (filterVisible === 'visible' && speaker.display_on_public) ||
                         (filterVisible === 'hidden' && !speaker.display_on_public);
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Speaker Management</h2>
        <button
          onClick={() => setIsAddingNew(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          Add Speaker
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex space-x-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search speakers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <select
          value={filterVisible}
          onChange={(e) => setFilterVisible(e.target.value as 'all' | 'visible' | 'hidden')}
          className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="all">All Speakers</option>
          <option value="visible">Public Visible</option>
          <option value="hidden">Hidden from Public</option>
        </select>
      </div>

      {/* Add New Speaker Form */}
      {isAddingNew && (
        <SpeakerForm
          speaker={newSpeaker}
          onSubmit={handleSubmitNew}
          submitLabel="Create Speaker"
          onFieldChange={handleFieldChange}
          onCancel={handleCancel}
          onImageUpload={onImageUpload}
        />
      )}

      {/* Speakers Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredSpeakers.map((speaker) => (
          <div
            key={speaker.id}
            className={`bg-white shadow rounded-lg p-6 border-l-4 ${
              speaker.display_on_public ? 'border-green-500' : 'border-gray-300'
            }`}
          >
            {editingSpeaker?.id === speaker.id ? (
              <SpeakerForm
                speaker={editingSpeaker}
                onSubmit={handleSubmitEdit}
                submitLabel="Update Speaker"
                onFieldChange={handleFieldChange}
                onCancel={() => setEditingSpeaker(null)}
                onImageUpload={onImageUpload}
              />
            ) : (
              <div>
                {/* Speaker Header */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex space-x-4">
                    <div className="flex-shrink-0">
                      {speaker.image ? (
                        <img
                          src={formatImageUrl(speaker.image)}
                          alt={speaker.name}
                          className="h-16 w-16 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-500 text-sm">No Photo</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">{speaker.name}</h3>
                      {speaker.title && (
                        <p className="text-sm text-gray-600">{speaker.title}</p>
                      )}
                      {speaker.company && (
                        <p className="text-sm text-gray-500">{speaker.company}</p>
                      )}
                      <div className="flex items-center mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          speaker.display_on_public 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {speaker.display_on_public ? 'Public' : 'Hidden'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingSpeaker(speaker)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDeleteSpeaker(speaker.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Speaker Details */}
                <div className="space-y-3">
                  {speaker.bio_short && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700">Short Bio</h4>
                      <p className="text-sm text-gray-600">{speaker.bio_short}</p>
                    </div>
                  )}
                  
                  {speaker.talk_title && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700">Talk</h4>
                      <p className="text-sm text-gray-900 font-medium">{speaker.talk_title}</p>
                      {speaker.talk_abstract && (
                        <p className="text-sm text-gray-600 mt-1">{speaker.talk_abstract}</p>
                      )}
                    </div>
                  )}

                  {speaker.linkedin_url && (
                    <div>
                      <a
                        href={speaker.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        LinkedIn Profile →
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredSpeakers.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">No speakers found matching your criteria.</p>
        </div>
      )}
    </div>
  );
};

export default SpeakerManagement;
